"use client"

import { useEffect, useRef, useState } from "react"
import { cn } from "@/lib/utils"

interface MeteorsProps {
  className?: string
  quantity?: number
  speed?: number
  size?: number
  color?: string
  opacity?: number
  minOpacity?: number
  speedVariance?: number
  sizeVariance?: number
}

export const Meteors = ({
  className,
  quantity = 20,
  speed = 10,
  size = 2,
  color = "#10b981", // default emerald-500
  opacity = 0.6,
  minOpacity = 0.1,
  speedVariance = 0.5,
  sizeVariance = 0.5,
}: MeteorsProps) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const [meteorElements, setMeteorElements] = useState<React.ReactNode[]>([])

  useEffect(() => {
    if (!containerRef.current) return

    const meteors = []
    const containerWidth = containerRef.current.offsetWidth
    const containerHeight = containerRef.current.offsetHeight

    for (let i = 0; i < quantity; i++) {
      const meteorSize = size * (1 + Math.random() * sizeVariance)
      const meteorSpeed = speed * (1 + Math.random() * speedVariance)
      const startPositionX = Math.random() * containerWidth
      const startPositionY = -meteorSize * 2
      const endPositionX = startPositionX - containerWidth * 0.3
      const endPositionY = containerHeight + meteorSize * 2
      const delay = Math.random() * 10
      const duration = (10 / meteorSpeed) * (1 + Math.random())
      const meteorOpacity = minOpacity + Math.random() * (opacity - minOpacity)

      meteors.push(
        <span
          key={`meteor-${i}`}
          className="absolute rounded-full shadow-xs transition-[opacity,transform]"
          style={{
            width: `${meteorSize}px`,
            height: `${meteorSize * 30}px`,
            left: `${startPositionX}px`,
            top: `${startPositionY}px`,
            background: `linear-gradient(to bottom, transparent, ${color})`,
            opacity: meteorOpacity,
            transform: `translate(${endPositionX - startPositionX}px, ${endPositionY - startPositionY}px) rotate(15deg)`,
            animation: `meteor ${duration}s ${delay}s linear infinite`,
          }}
        />
      )
    }

    setMeteorElements(meteors)
  }, [quantity, speed, size, color, opacity, minOpacity, speedVariance, sizeVariance])

  return (
    <div
      ref={containerRef}
      className={cn("absolute inset-0 overflow-hidden pointer-events-none", className)}
      aria-hidden="true"
    >
      <style jsx global>{`
        @keyframes meteor {
          0% {
            transform: translate(0, 0) rotate(15deg);
            opacity: 0;
          }
          5% {
            opacity: var(--meteor-opacity, ${opacity});
          }
          90% {
            opacity: var(--meteor-opacity, ${opacity});
          }
          100% {
            transform: translate(var(--meteor-x, -30%), var(--meteor-y, 200%)) rotate(15deg);
            opacity: 0;
          }
        }
      `}</style>
      {meteorElements}
    </div>
  )
}
